// Constrói a lista de pernas (legs) de uma rota associada a OTs, no
// formato que o painel inferior de monitorização do AppLayout consome.
//
// Este algoritmo é uma versão simplificada do que está em
// OrdersView.loadOTOnMap (que faz waypoint-walking + cálculo cumulativo
// de km via haversine). Para o caso "carregar saved route no
// Planeamento" não precisamos da precisão do walking — uma leg por OT
// com nomes encadeados (origem da rota → 1ª OT → 2ª OT…) é suficiente
// para o painel inferior aparecer e ficar consistente com o que vem do
// ecrã das OTs.
//
// Inputs:
//   route   — objecto vindo de GET /planner-api/routes/:id (contém
//             routeData, origin, vehicle, driver, totalDistanceM…)
//   orders  — array de OTs (transport_orders) atribuídas à rota
//
// Output: { routeLegs, orderedOTIds } — formato idêntico ao usado
// por AppLayout (ver setMonitoringLegs em [AppLayout.js:583]).

export function buildRouteLegs(route, orders) {
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  if (list.length === 0) return { routeLegs: [], orderedOTIds: [] };

  const routeOriginName =
    route?.origin?.text
    || route?.origin?.name
    || route?.origin?.address
    || 'Origem da rota';

  // Ordena pela janela de entrega (fallback à de pickup e à data de
  // criação) — mesma heurística da fallback-branch em loadOTOnMap.
  const sorted = [...list].sort((a, b) => {
    const ta = new Date(a.deliveryWindowFrom || a.pickupWindowFrom || a.createdAt || 0).getTime();
    const tb = new Date(b.deliveryWindowFrom || b.pickupWindowFrom || b.createdAt || 0).getTime();
    return ta - tb;
  });

  const orderedOTIds = sorted.map(o => o.id || o._id);

  const routeLegs = sorted.map((o, i) => ({
    orderId: o.id || o._id,
    orderNumber: o.orderNumber,
    // Distância/duração cacheadas por OT (otLeg*) quando existirem;
    // senão deixa null para o painel mostrar "—".
    distanceKm: o.otLegDistanceM != null ? o.otLegDistanceM / 1000 : null,
    durationMin: o.otLegDurationS != null ? Math.round(o.otLegDurationS / 60) : null,
    // A primeira OT parte da origem da rota; as seguintes do destino da
    // anterior. Sem isto cada leg dizia "Origem → X" o que era enganador.
    pickupName: i === 0
      ? routeOriginName
      : (sorted[i - 1].deliveryName || sorted[i - 1].deliveryAddress || ''),
    deliveryName: o.deliveryName || o.deliveryAddress || '',
    status: o.status,
  }));

  return { routeLegs, orderedOTIds };
}

// Constrói um objecto "monitoringOT" sintético para o painel inferior
// quando entramos em inspect-mode SEM uma OT específica em foco (ex.:
// carregar saved route no Planeamento). O painel usa o orderNumber/
// vehicle/driver no cabeçalho — preenchemos a partir da rota e da
// primeira OT para que o aspecto seja coerente com Path A.
export function buildMonitoringRouteSummary(route, firstOrder) {
  if (!route) return null;
  return {
    orderId: firstOrder?.id || firstOrder?._id || null,
    orderNumber: firstOrder?.orderNumber || route.name || `Rota #${route.id}`,
    vehicleId: route.vehicleId
      || route.vehicle?.id
      || route.routeData?.vehicleInfo?.id
      || null,
    vehiclePlate: route.vehicle?.plate
      || route.vehiclePlate
      || route.routeData?.vehicleInfo?.plate
      || '',
    driverName: route.driver?.name
      || route.driverName
      || route.routeData?.driverInfo?.name
      || '',
    status: firstOrder?.status || route.status || 'unknown',
    pickupName: firstOrder?.pickupName || firstOrder?.pickupAddress || '',
    deliveryName: firstOrder?.deliveryName || firstOrder?.deliveryAddress || '',
    deliveryWindowFrom: firstOrder?.deliveryWindowFrom || null,
    deliveryWindowTo: firstOrder?.deliveryWindowTo || null,
    estimatedArrival: firstOrder?.estimatedArrival || null,
    distanceKm: firstOrder?.distanceKm
      || (route.totalDistanceM ? route.totalDistanceM / 1000 : null),
    actualStartAt: firstOrder?.actualStartAt || route.routeData?.actualStartedAt || null,
  };
}
